import fetch from "cross-fetch";

import { BASE_LOCALE } from "../constants";
import {
  APP_HOSTNAME,
  CF_ACCESS_CLIENT_ID,
  CF_ACCESS_CLIENT_SECRET,
  isProduction,
  TRANSLATIONS_ENDPOINT,
} from "../Env";

const GOOGLE_TRANSLATE_PARAGRAPH_LIMIT = 128;

/**
 * Initialize a function for sending text through the Google Translate API.
 * @param locale The locale to use for translating.
 * @param shouldTranslate If falsy, skip sending the request.
 * @returns The translation function
 */
export function getMachineTranslation(
  locale: string | null,
  shouldTranslate: boolean = true
) {
  const localeString = locale || BASE_LOCALE;
  return async (baseText: string | null | undefined) => {
    const text = baseText || "";
    if (!(baseText && shouldTranslate) || localeString === BASE_LOCALE) {
      return text;
    }
    const paragraphs = text.split("\n");
    let translatedLines: string[] = [];
    for (
      let i = 0;
      i < paragraphs.length;
      i += GOOGLE_TRANSLATE_PARAGRAPH_LIMIT
    ) {
      const paragraphBatch = paragraphs.slice(
        i,
        i + GOOGLE_TRANSLATE_PARAGRAPH_LIMIT
      );
      const translatedLinesBatch = await googleTranslateLines(
        paragraphBatch,
        localeString
      );
      translatedLines = translatedLines.concat(translatedLinesBatch);
    }

    return translatedLines.join("\n");
  };
}

async function googleTranslateLines(
  text: string[],
  targetLocale: string
): Promise<string[]> {
  const response = await fetch(TRANSLATIONS_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": `application/json; charset="utf-8"`,
      Referer: APP_HOSTNAME
        ? APP_HOSTNAME
        : isProduction
        ? "https://www.outschool.com"
        : "https://staging.outschool.dev",
      ...(CF_ACCESS_CLIENT_ID
        ? { "CF-Access-Client-Id": CF_ACCESS_CLIENT_ID }
        : {}),
      ...(CF_ACCESS_CLIENT_SECRET
        ? { "CF-Access-Client-Secret": CF_ACCESS_CLIENT_SECRET }
        : {}),
    },
    body: JSON.stringify({
      q: text,
      target: targetLocale,
    }),
  });
  if (response.status >= 400) {
    const err = new Error(
      `GTranslate CF worker error code: ${response.status}`
    );
    // include roughly the first KB of the response body
    (err as any).body = (await response.text()).slice(0, 1024);
    throw err;
  }
  const { data } = (await response.json()) as any;
  const translatedText = data.translations.map(
    (translation: { translatedText: string }) =>
      cleanUpTranslation(translation.translatedText)
  );
  return translatedText;
}

function cleanUpTranslation(str: string) {
  return str
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, ",")
    .replace(/&amp;/g, "&");
}
